import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Inverter } from '../../shared';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
} from '@mui/material';

import { trpc } from '../../api/trpc';
import { useSelector } from '../../store/utils';

import {
  boardsWithInternalNetwork,
  ConnectionType,
  invertersSupportingRS485,
  NetworkType,
} from '../../../../core/src/shared/enums';
import { InverterTypeSelect } from './inverter-type-select';
import { ConnectionTypeSelect } from './connection-type-select';
import { NetworkTypeSelect } from './network-type-select';
import { ExternalNetworkHostsScan } from './external-network-hosts-scan';
import { InternalNetworkHosts } from './internal-network-hosts';
import { InverterParameters } from './inverter-parameters';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

const invertersRequiringNominalPower = [
  Inverter.SolarEdgeGrid,
  Inverter.GoodWeGridNext,
  Inverter.SolaxMICGrid,
];

type Props = {
  open: boolean;
  onClose: () => void;
};

export const InverterWizard = ({ open, onClose }: Props) => {
  const [board, inverterConfig] = useSelector(
    ({ variables }) => [variables.BOARD, variables.INVERTER_CONFIG] as const
  );

  const internalNetworkSupported = boardsWithInternalNetwork.includes(board);

  const defaultNetworkType = internalNetworkSupported
    ? NetworkType.Internal
    : NetworkType.External;

  const defaultDeviceIpAddress = internalNetworkSupported
    ? '100.100.1.100'
    : undefined;

  const [inverterType, setInverterType] = useState<Inverter>(
    Inverter.SolaxNext
  );
  const [connectionType, setConnectionType] = useState(
    ConnectionType.ModbusTCP
  );
  const [networkType, setNetworkType] = useState(defaultNetworkType);
  const [multipleInverters, setMultipleInverters] = useState(false);
  const [internalNetworkDevice, setInternalNetworkDevice] = useState<
    { mac: string; ip: string } | undefined
  >();
  const [deviceIpAddress, setDeviceIpAddress] = useState(
    defaultDeviceIpAddress
  ); // custom network
  const [deviceMac, setDeviceMac] = useState<string | undefined>(); // external network
  const [nominalPower, setNominalPower] = useState('');

  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [submitErrorDetails, setSubmitErrorDetails] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  useEffect(() => {
    setDeviceIpAddress(defaultDeviceIpAddress);
  }, [defaultDeviceIpAddress]);

  const wizardDisabledMultipleInvertersOrEMS =
    Object.keys(inverterConfig).length > 1 ||
    Object.values(inverterConfig).some(({ type }) =>
      [false].includes(type as any)
    ); // to be implemented in EMS1000 branch

  const connectionTypeVisible = ![
    Inverter.None,
    Inverter.SofarSolarRS485,
    Inverter.Hes,
  ].includes(inverterType);

  const rs485Supported = invertersSupportingRS485.includes(inverterType);

  const networkTypeSelectVisible =
    connectionType !== ConnectionType.RS485 &&
    inverterType !== Inverter.SofarSolarRS485 && // exception for "only RS485" Sofar
    inverterType !== Inverter.Hes && // exception for "only RS485" HES
    inverterType !== Inverter.None;

  const { mutate, isLoading } = trpc.inverter.wizardSubmit.useMutation({
    onMutate() {
      setSubmitError(undefined);
      setSubmitErrorDetails(false);
    },
    onSuccess() {
      onClose();
    },
    onError(error) {
      setSubmitError(error.message);
    },
  });

  const submitDisabled =
    isLoading || multipleInverters || wizardDisabledMultipleInvertersOrEMS;
  const nominalPowerRequired =
    invertersRequiringNominalPower.includes(inverterType);
  const nominalPowerValue =
    nominalPower.trim() === '' ? undefined : Number(nominalPower);
  const nominalPowerValid =
    nominalPowerValue !== undefined && !Number.isNaN(nominalPowerValue);

  const handleSubmit = () => {
    setShowValidationErrors(true);

    if (
      connectionType === ConnectionType.ModbusTCP &&
      networkType === NetworkType.Custom &&
      !deviceIpAddress &&
      inverterType !== Inverter.None
    ) {
      return;
    }

    if (
      connectionType === ConnectionType.ModbusTCP &&
      networkType === NetworkType.Internal &&
      !internalNetworkDevice &&
      inverterType !== Inverter.None
    ) {
      return;
    }

    if (
      connectionType === ConnectionType.ModbusTCP &&
      networkType === NetworkType.External &&
      !deviceMac &&
      inverterType !== Inverter.None
    ) {
      return;
    }

    if (nominalPowerRequired && !nominalPowerValid) {
      return;
    }

    mutate({
      inverterType,
      connectionType,
      networkType,
      deviceMac, // external network
      deviceIpAddress, // custom network
      internalNetworkDevice, // internal network
      nominalPower: nominalPowerRequired ? nominalPowerValue : undefined,
    } as any);
  };

  const wizardForm = (
    <Stack gap={2.5} sx={{ py: 2 }}>
      <InverterTypeSelect
        value={inverterType}
        onChange={(type) => {
          setInverterType(type);
          setNominalPower('');
          setShowValidationErrors(false);

          if (type === Inverter.SofarSolarRS485 || type === Inverter.Hes) {
            return setConnectionType(ConnectionType.RS485);
          }

          setConnectionType(ConnectionType.ModbusTCP);
        }}
      />
      <InverterParameters
        inverterType={inverterType}
        nominalPower={nominalPower}
        onNominalPowerChange={setNominalPower}
        showErrors={showValidationErrors}
      />
      {connectionTypeVisible && (
        <ConnectionTypeSelect
          value={connectionType}
          onChange={setConnectionType}
          rs485Supported={rs485Supported}
        />
      )}

      {networkTypeSelectVisible && (
        <NetworkTypeSelect
          value={networkType}
          onChange={(value) => {
            setDeviceMac(undefined);
            setDeviceIpAddress(defaultDeviceIpAddress);
            setNetworkType(value);
          }}
        />
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={multipleInverters}
            onChange={(e) => setMultipleInverters(e.target.checked)}
          />
        }
        label={
          <FormattedMessage
            id="inverter_wizard.multiple_inverters.label"
            defaultMessage="Více střídačů"
          />
        }
        sx={{ mt: -1 }}
      />

      {connectionType === ConnectionType.ModbusTCP &&
        !multipleInverters &&
        networkType === NetworkType.External &&
        networkTypeSelectVisible && (
          <ExternalNetworkHostsScan value={deviceMac} onChange={setDeviceMac} />
        )}

      {connectionType === ConnectionType.ModbusTCP &&
        !multipleInverters &&
        networkType === NetworkType.Internal &&
        networkTypeSelectVisible && (
          <InternalNetworkHosts
            value={internalNetworkDevice}
            onChange={setInternalNetworkDevice}
          />
        )}

      {connectionType === ConnectionType.ModbusTCP &&
        !multipleInverters &&
        networkType === NetworkType.Custom &&
        networkTypeSelectVisible && (
          <>
            <TextField
              label={
                <FormattedMessage
                  id="inverter_wizard.custom_device_ip_address.input"
                  defaultMessage="IP adresa střídače"
                />
              }
              value={deviceIpAddress}
              onChange={(e) => setDeviceIpAddress(e.target.value)}
              fullWidth
            />
            <Alert icon={false} color="warning">
              <FormattedMessage
                id="inverter_wizard.custom_device_ip_address.alert"
                defaultMessage="Ujistěte se prosím, že se IP adresa střídače nebude měnit (např. když střídač využívá adresu z DHCP)."
              />
            </Alert>
          </>
        )}

      {multipleInverters && (
        <>
          <Alert icon={false} color="warning">
            <FormattedMessage
              id="inverter_wizard.multiple_inverters.alert"
              defaultMessage="Pro nastavení více střídačů prosím kontaktujte linku podpory. Kontakt najdete v technické wiki Infigy."
            />
          </Alert>
        </>
      )}

      {submitError && (
        <Alert severity="error" icon={false}>
          <FormattedMessage
            id="inverter_wizard.submit_error"
            defaultMessage="Nastala chyba při ukládání nastavení střídače. Zkuste to prosím znovu."
          />

          <IconButton
            size="small"
            onClick={() => setSubmitErrorDetails((prev) => !prev)}
            sx={{ ml: 0.25 }}
          >
            {submitErrorDetails ? <ExpandLess /> : <ExpandMore />}
          </IconButton>

          <Collapse in={submitErrorDetails}>
            <Box sx={{ pt: 2 }}>{submitError}</Box>
          </Collapse>
        </Alert>
      )}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle variant="h2">
        <FormattedMessage
          id="inverter_wizard.title"
          defaultMessage="Průvodce připojením střídače"
        />
      </DialogTitle>
      <DialogContent>
        {wizardDisabledMultipleInvertersOrEMS ? (
          <Alert icon={false} color="warning">
            <FormattedMessage
              id="inverter_wizard.multiple_inverters_or_ems.alert"
              defaultMessage="Váš systém již obsahuje nastavení pro více střídačů nebo EMS. Tento průvodce je určen pouze pro nastavení jednoho střídače. Pro změnu nastavení střídačů nebo EMS prosím kontaktujte podporu."
            />
          </Alert>
        ) : (
          wizardForm
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage
            id="inverter_wizard.cancel"
            defaultMessage="Zrušit"
          />
        </Button>
        <Button
          variant="contained"
          disabled={submitDisabled}
          onClick={handleSubmit}
        >
          <FormattedMessage id="inverter_wizard.save" defaultMessage="Uložit" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
